import React, { useState} from "react";
import Slider from "react-slick";
import { ArrowRightIcon } from 'lucide-react';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./funds.css"; // You'll need to create this for the glass effect
import { Link } from "react-router-dom";

const FundCard = ({ title, description, onLearnMore, link }) => (
  <div className="glass-card glass-card-medium mx-2 sm:mx-4 flex flex-col min-h-[300px] sm:min-h-[400px] glass-hover-scale">
    <h3 className="glass-card-title text-base sm:text-lg lg:text-xl mb-3">{title}</h3>
    <div className="flex-1 mb-4">
      <p className="glass-card-text text-sm sm:text-base leading-relaxed">{description}</p>
    </div>
    {link ? (
      link.startsWith('http') ? (
        <a 
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="glass-btn glass-btn-primary text-sm sm:text-base mt-auto"
        >
          Join Now <ArrowRightIcon className="ml-2" size={16} />
        </a>
      ) : (
        <Link
          to={link}
          className="glass-btn glass-btn-primary text-sm sm:text-base mt-auto"
        >
          Join Us <ArrowRightIcon className="ml-2" size={16} />
        </Link>
      )
    ) : (
      <button
        onClick={onLearnMore}
        className="glass-btn glass-btn-secondary text-sm sm:text-base mt-auto"
      >
        Learn More <ArrowRightIcon className="ml-2" size={16} />
      </button>
    )}
  </div>
);

const PopUp = ({ isOpen, onClose, title, description, points, onJoin, link, external }) => {
  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content glass-card glass-card-large">
        <h2 className="glass-card-title text-xl sm:text-2xl mb-4">{title}</h2>
        <div className="mb-4">
          <p className="glass-card-text mb-4 text-sm sm:text-base">{description}</p>
          {points && points.length > 0 ? (
            <ul className="list-disc pl-5 mb-4">
              {points.map((point, index) => (
                <li key={index} className="glass-card-text mb-2 text-sm sm:text-base">{point}</li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-2">
          {onJoin && (
            external ? (
              <a
                href={link} // Use the link prop
                target="_blank"
                rel="noopener noreferrer"
                onClick={onJoin}
                className="glass-btn glass-btn-primary text-center text-sm sm:text-base"
              >
                Join Now
              </a>
            ) : (
              <Link
                to='/contact'
                onClick={onJoin}
                className="glass-btn glass-btn-primary text-center text-sm sm:text-base"
              >
                Join Now
              </Link>
            )
          )}
          <button
            onClick={onClose}
            className="glass-btn text-sm sm:text-base"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const FundsPage = () => {
  
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);


  const funds = [
    {
      title: "Tier 1: Hands-Off PAMM (Auto-Pilot)",
      description: "Hands-Off Wealth Building on Auto-Pilot. Designed for passive investors who want zero software setup, no VPS management, and pure hands-off exposure to our institutional-grade algorithms.",
      points: [
        "Zero Setup & No VPS Fees",
        "Minimum Capital: $250 - $500",
        "20% Performance Fee (Net Profit Split via High-Water Mark)",
        "Fully automated execution via Partner Broker IB link",
        "100% Hands-off - No trading experience required"
      ],
      link: "/pamm-pool"
    },
    {
      title: "Private Strategy Partnership",
      description: "Bespoke algorithmic portfolio integration for family offices and private funds. We deploy our proprietary ML-driven execution infrastructure directly onto your existing brokerage or prime-broker architecture.",
      points: [
        "100% Capital Control - execution on your own infrastructure",
        "API / Webhook execution via institutional platforms",
        "Dedicated portfolio allocation with customized risk mandates",
        "Real-time transparent reporting and performance tracking",
        "Quarterly Performance Licensing or Profit Share agreements"
      ]
    },
    {
      title: "Institutional Infrastructure Licensing",
      description: "License our quantitative models and automated execution pipelines for your proprietary trading desk. Turn-key algorithmic solutions tailored to your specific mandates.",
      points: [
        "White-label deployment for proprietary trading firms",
        "Custom ONNX ML pipelines for predictive execution",
        "Ongoing quantitative maintenance and model retraining",
        "High-Water Mark (HWM) profit share agreements",
        "Scalable execution across multiple asset classes"
      ]
    },
    {
      title: "Bespoke Algorithmic Development",
      description: "Custom quantitative strategy development and automation. We convert your proven discretionary edge into a fully automated, backtested algorithmic pipeline.",
      points: [
        "Strict IP Protection and Non-Disclosure Agreements (NDAs)",
        "Institutional-grade backtesting and forward testing",
        "Server-side execution for latency optimization",
        "Integration with MetaTrader, cTrader, or custom APIs",
        "Full handover of compiled execution logic"
      ]
    },
    {
      title: "Prime Broker Integration",
      description: "Our quantitative infrastructure is designed to integrate seamlessly with institutional liquidity providers and prime brokerages. We ensure optimal execution environments for our algorithms.",
      points: [
        "Direct Market Access (DMA) compatibility",
        "Institutional liquidity and tight spread optimization",
        "Full regulatory compliance and secure API bridging",
        "No guarantee of performance - all trading involves risk",
        "Detailed terms available in institutional SLA"
      ]
    }
  ];

  const handleLearnMore = (fund) => {
    // Add additional details for Institutional funds
    const enhancedFund = { ...fund };
    if (fund.title === "Private Strategy Partnership") {
      enhancedFund.description += " Our proprietary algorithms employ AI and machine learning for precise, data-driven trading across Forex, Stocks, Commodities, and Crypto markets. Our models dynamically adjust to real-time market conditions through secure API bridging.";
    }
    setSelectedFund(enhancedFund);
    setPopUpOpen(true);
  };

  const handleJoin = () => {
    console.log("Joining fund:", selectedFund.title);
    setPopUpOpen(false);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 20000,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          centerMode: true,
          centerPadding: '20px',
        }
      }
    ]
  };

  function NextArrow(props) {
    const { onClick } = props;
    return (
      <div
        className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-blue-500 p-2 rounded-full cursor-pointer hover:bg-blue-700 z-10"
        onClick={onClick}
      >
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M10 18l6-6-6-6"></path>
        </svg>
      </div>
    );
  }

  function PrevArrow(props) {
    const { onClick } = props;
    return (
      <div
        className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-blue-500 p-2 rounded-full cursor-pointer hover:bg-blue-700 z-10"
        onClick={onClick}
      >
        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14 18l-6-6 6-6"></path>
        </svg>
      </div>
    );
  }

  return (
    <section
      className="relative min-h-screen w-full py-8 sm:py-16"
    >
      <div className="w-full max-w-6xl mx-auto px-4">
        <div className="relative">
          <Slider {...sliderSettings}>
            {funds.map((fund, index) => (
              <div key={index} className="px-2">
                <FundCard
                  title={fund.title}
                  description={fund.description}
                  onLearnMore={() => handleLearnMore(fund)}
                  link={fund.link}
                />
              </div>
            ))}
          </Slider>
        </div>

        {/* Verified by ProTrader Section */}
        <div className="mt-16 sm:mt-24 w-full">
          <div className="glass-card glass-card-large p-8 sm:p-12 text-center flex flex-col items-center justify-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-white tracking-wide">
              Live Portfolio Analytics
            </h2>
            <p className="text-base sm:text-lg text-gray-300 max-w-3xl leading-relaxed mb-8">
              All algorithmic execution is monitored in real-time by our proprietary analytics infrastructure, <strong className="text-white">ProTrader Visual Journal</strong>. We provide qualified institutional partners with read-only guest access to view our live equity curves, Sharpe ratios, and granular trade-by-trade metrics.
            </p>
            <Link
              to="/contact"
              className="glass-btn glass-btn-primary text-base sm:text-lg px-8 py-3"
            >
              Request Dashboard Access <ArrowRightIcon className="inline ml-2" size={20} />
            </Link>
          </div>
        </div>
      </div>
      <PopUp
        isOpen={popUpOpen}
        onClose={() => setPopUpOpen(false)}
        title={selectedFund?.title}
        description={selectedFund?.description}
        points={selectedFund?.points}
        onJoin={handleJoin}
        link={selectedFund?.link}
        external={selectedFund?.external}
      />
    </section>
  );
};

export default FundsPage;
